// src/components/EventPage.js
// (Split into 3 files: EventPage + MediaCard + StorybookDrawer)
// ✅ No logic changes — just extracted components + lazy loaded Storybook drawer.
// ✅ Enhancements added (minimal, performance-only):
//   - Preload first N grid images via new Image() + decode() using requestIdleCallback
//   - requestIdleCallback warmups (image decode + next-page prefetch nudge)
//   - More aggressive next-page prefetch (scroll threshold + IO sentinel w/ large rootMargin)
//   - Defensive "disable content-visibility/containment" on the gallery container
//
// ✅ NEW (requested):
//   - Instagram-style hamburger menu (white icon, top-right)
//   - Menu drawer is lazy-loaded and connects to EventMenuDrawer + EventShareSheet (inside drawer)

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useDeferredValue,
} from "react";
import { VIBES, VibeSelector, VibeFilterBar } from "../components/Vibefilter";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  IconButton,
} from "@mui/material";

import {
  db,
  auth,
  functions,
} from "../firebase/firebaseConfig";
import {
  doc,
  onSnapshot,
  updateDoc,
  collection,
  addDoc,
  query,
  orderBy,
  deleteDoc,
  getDocs,
  getDoc,
  where,
  limit as fbLimit,
  startAfter,
  serverTimestamp,
  getCountFromServer,
} from "firebase/firestore";

import uploadMedia from "../firebase/uploadMedia";
import "../styles/EventPage.css";

import { ensureAnonUser } from "../firebase/ensureAnonUser";
import { onAuthStateChanged } from "firebase/auth";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";


import { isUnlocked, setUnlocked } from "../utils/authHelpers";

import EventAccessUI, { getGalleryState } from "./EventAccessUI";
import { planPriceIds } from "../components/EventLimitations";



import LiveCoverHero from "./LiveCoverHero";
import { STYLE_PRESETS } from "./CoverStylePicker";
import { startStorybookCheckout } from "../api/storybook";
import { startBoomerangRender } from "../api/boomerang";
import RevealCountdownBanner from "../components/RevealCountdownBanner";

import MediaCard from "./MediaCard";
import { getStorage, ref as storageRef, uploadBytesResumable } from "firebase/storage";
import EventPageSkeleton from "./EventPageSkeleton";
import HiddenGalleryNotice from "./HiddenGalleryNotice";
import StrictSurprisePreview from "./StrictSurprisePreview";
import StorybookDrawer from "./StorybookDrawer";

const EmailGate = React.lazy(() => import("../components/EmailGate"));
const CameraScreen = React.lazy(() => import("./WebCameraScreen"));
const EventMenuDrawer = React.lazy(() => import("./EventMenuDrawer"));

const TemplateFlowLauncher = React.lazy(() =>
  import("./Templates/TemplateFlowLauncher")
);
const FullscreenMediaViewer = React.lazy(() =>
  import("./FullscreenMediaViewer")
);
const ExportFlowDrawer = React.lazy(() =>
  import("./ExportFlowDrawer")
);
// ------------------------------------
// Small utilities
// ------------------------------------
const withTimeout = (promise, ms, label = "operation") =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out`)), ms)
    ),
  ]);
  const wait = (ms) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const withOneSilentRetry = async (
  task,
  label = "operation"
) => {
  try {
    return await withTimeout(
      task(),
      12000,
      `${label} first attempt`
    );
  } catch (firstError) {
    console.warn(
      `${label} first attempt failed. Retrying...`,
      firstError
    );

    const retryDelay = 800 + Math.random() * 700;
    await wait(retryDelay);

    return await withTimeout(
      task(),
      10000,
      `${label} retry`
    );
  }
};
async function resolveEventDocIdFromSlug(slug) {
  const raw = String(slug || "").trim();
  if (!raw) return null;

  let directLookupError = null;

  // Fast path: current QR/share links should use the Firestore document ID.
  try {
    const directSnap = await getDoc(doc(db, "events", raw));

    if (directSnap.exists()) {
      return directSnap.id;
    }
  } catch (error) {
    directLookupError = error;
    console.warn("Direct event lookup failed:", error);
  }

  // Fallback for older links that used uniqueUrl or slug.
  try {
    const eventsCol = collection(db, "events");

    const uniqueSnap = await getDocs(
      query(
        eventsCol,
        where("uniqueUrl", "==", raw),
        fbLimit(1)
      )
    );

    if (!uniqueSnap.empty) {
      return uniqueSnap.docs[0].id;
    }

    const slugSnap = await getDocs(
      query(
        eventsCol,
        where("slug", "==", raw),
        fbLimit(1)
      )
    );

    if (!slugSnap.empty) {
      return slugSnap.docs[0].id;
    }
  } catch (error) {
    console.warn("Fallback event lookup failed:", error);

    // A network/Firestore failure should trigger the silent retry.
    throw error;
  }

  // If the direct lookup failed because of the network, do not call it
  // "not found." Throw the error so the silent retry can run.
  if (directLookupError) {
    throw directLookupError;
  }

  // All lookups completed successfully, but no event matched.
  return null;
}

const UPLOAD_MAX_SIDE = 2400; // sharp “viewer/source” image
const THUMB_MAX_SIDE = 512;
const THUMB_MAX_MB = 0.35;
const MAX_PHOTO_MB = 5;
const MAX_VIDEO_MB = 80;
const MAX_VIDEO_SEC = 60;
const PAGE_SIZE = 24;
const STICKY_VIBE_BAR_H = 64;

// ✅ perf knobs
const PREFETCH_SCROLL_RATIO = 0.5;
const PREFETCH_IO_ROOT_MARGIN = "1200px 0px";
const PREFETCH_IDLE_TIMEOUT_MS = 700; // idle nudge window

const bytes = (mb) => mb * 1_048_576;

const tooBigRaw = (f) =>
  f.size > (f.type.startsWith("image/") ? bytes(25) : bytes(MAX_VIDEO_MB));

const isHttpish = (u) =>
  typeof u === "string" && /^https?:\/\//i.test(u);

const isLocalBlobUrl = (u) =>
  typeof u === "string" && u.startsWith("blob:");

const isDisplayableUrl = (u) =>
  isHttpish(u) || isLocalBlobUrl(u);

const isRenderablePhoto = (x) => {
  if (x?.type !== "photo") return false;

  const stillOk =
  isDisplayableUrl(x?.url) ||
  isDisplayableUrl(x?.thumbnailUrl);

  if (x?.isBoomerang) {
    return (
      stillOk ||
      isHttpish(x?.boomerangUrl) ||
      isLocalBlobUrl(x?.boomerangLocalUrl)
    );
  }

  return stillOk;
};

const isRenderableVideo = (x) => x.type === "video" && isHttpish(x.url);

const validateInitial = async (files) => {
  const ok = [];
  const errs = [];

  for (const f of files) {
    const isImg = f.type.startsWith("image/");
    if (!isImg) {
      errs.push(`${f.name} (Photos only — use POV Shot for boomerang)`);
      continue;
    }
    if (tooBigRaw(f)) {
      errs.push(`${f.name} (Too large)`);
      continue;
    }
    ok.push(f);
  }
  return { ok, errs };
};

// Guest limits
const guestLimits = {
  price_1R85uFRtiroMBcDI97u58KNs: 20,
  price_1RBQRIRtiroMBcDIUchpI0FF: 35,
  price_1R85QVRtiroMBcDIy7rg25mQ: 35,
  price_1R85VSRtiroMBcDIXv9u0P6q: 60,
  price_1Su18IRtiroMBcDIHLnrAM3s: 60,
  price_1UWiw6RtiroMBcDIIz7RoMNp: 60, // Holiday Special
  price_1R85YsRtiroMBcDIi5iHkGUb: 130,
  price_1R85wBRtiroMBcDIf4HpIbr7: 260,
  price_1SZHdCRtiroMBcDIY5gWTouf: 700,
};
const UNPAID_GUEST_LIMIT = 20;

const resolvePlanPriceId = (event) => {
  if (!event) return null;
  const candidates = [
    event.planPriceId,
    event.priceId,
    event.customization?.planPriceId,
    event.customization?.priceId,
  ].filter(Boolean);
  return candidates.find((id) => planPriceIds.includes(id)) || null;
};

const getAllowedGuests = (event) => {
  const pid = resolvePlanPriceId(event);
  return pid ? (guestLimits[pid] ?? UNPAID_GUEST_LIMIT) : UNPAID_GUEST_LIMIT;
};

const cropAndResizeImage = (file, maxSide = UPLOAD_MAX_SIDE, targetMaxMB = MAX_PHOTO_MB) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.decoding = "async";
      img.loading = "eager";

      img.onload = async () => {
        try {
          const w = img.naturalWidth || img.width;
          const h = img.naturalHeight || img.height;

          // ✅ If already within bounds AND size, don’t re-encode (prevents blur)
          const withinSide = Math.max(w, h) <= maxSide;
          const withinSize = file.size <= bytes(targetMaxMB);
          if (withinSide && withinSize) {
            resolve(file);
            return;
          }

          const scale = Math.min(1, maxSide / Math.max(w, h));
          const tw = Math.max(1, Math.round(w * scale));
          const th = Math.max(1, Math.round(h * scale));

          const canvas = document.createElement("canvas");
          canvas.width = tw;
          canvas.height = th;

          const ctx = canvas.getContext("2d", { alpha: false });
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(img, 0, 0, tw, th);

          // ✅ Start higher quality; only reduce if needed
          let quality = 0.95;
          let blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));

          while (blob && blob.size > bytes(targetMaxMB) && quality > 0.82) {
            quality -= 0.03;
            // eslint-disable-next-line no-await-in-loop
            blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
          }

          if (!blob) return reject(new Error("Canvas export failed"));

          const safeName = (file.name || "photo.jpg").replace(/\.(png|gif|bmp|webp)$/i, ".jpg");
          resolve(new File([blob], safeName, { type: "image/jpeg" }));
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = (err) => reject(err);
      img.src = e.target.result;
    };

    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });

const handleDownloadMedia = async (url, filename) => {
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const isVideo = /\.(mp4|webm)$/i.test(filename || "");

  // Keep the existing photo behavior.
  if (isIOS && !isVideo) {
    alert('To save this photo on iOS:\n\nLong-press the image, then tap “Add to Photos.”');
    return;
  }

  try {
    // Fetch the file so the download uses the correct filename and type.
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Download failed: ${response.status}`);

    const blob = await response.blob();
    const fileType = isVideo
      ? filename.toLowerCase().endsWith(".webm")
        ? "video/webm"
        : "video/mp4"
      : blob.type || "image/jpeg";

    if (isIOS && isVideo) {
      const file = new File([blob], filename, { type: fileType });

      if (
        typeof navigator.share === "function" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({ files: [file] });
        return;
      }

      alert("This iPhone could not open the video share sheet.");
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "photo.jpg";
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } catch (error) {
    if (error?.name === "AbortError") return; // Guest closed the share sheet.
    console.error("Download failed:", error);
    alert("Could not save this file. Please try again.");
  }
};

const computeEndMsFromEventDateLocal = (eventDateRaw) => {
  const s = String(eventDateRaw || "").trim();
  if (!s) return null;

  const m1 = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  const m2 = s.match(/^(\d{1,2})[-/](\d{1,2})$/);

  const now = new Date();
  let y, mo, d;

  if (m1) {
    y = Number(m1[1]);
    mo = Number(m1[2]);
    d = Number(m1[3]);
  } else if (m2) {
    mo = Number(m2[1]);
    d = Number(m2[2]);
    const candidateThisYear = new Date(now.getFullYear(), mo - 1, d, 23, 59, 59, 999);
    y = candidateThisYear.getTime() < now.getTime() ? now.getFullYear() + 1 : now.getFullYear();
  } else {
    return null;
  }

  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return new Date(y, mo - 1, d, 23, 59, 59, 999).getTime();
};

const getIntentionalFallbackCover = (eventName = "Your Event") => {
  const safeTitle = String(eventName).replace(/&/g, "and").slice(0, 40);
  const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="1200">
    <defs>
      <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#0f172a"/>
        <stop offset="0.55" stop-color="#111827"/>
        <stop offset="1" stop-color="#1f2937"/>
      </linearGradient>
      <filter id="grain">
        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" stitchTiles="stitch" />
        <feColorMatrix type="matrix" values="
          0 0 0 0 0.7
          0 0 0 0 0.7
          0 0 0 0 0.7
          0 0 0 .06 0"/>
      </filter>
    </defs>

    <rect width="100%" height="100%" fill="url(#g)"/>
    <rect width="100%" height="100%" filter="url(#grain)" opacity="0.45"/>

    <radialGradient id="r" cx="35%" cy="30%" r="65%">
      <stop offset="0" stop-color="rgba(255,255,255,0.14)"/>
      <stop offset="1" stop-color="rgba(255,255,255,0)"/>
    </radialGradient>
    <rect width="100%" height="100%" fill="url(#r)"/>

    <rect x="44" y="44" width="1112" height="1112" fill="none"
      stroke="rgba(255,255,255,0.18)" stroke-width="3" rx="40"/>

    <text x="50%" y="52%" text-anchor="middle"
      font-family="Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial"
      font-size="54" font-weight="700" fill="rgba(255,255,255,0.92)">
      ${safeTitle}
    </text>

    <text x="50%" y="58%" text-anchor="middle"
      font-family="Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial"
      font-size="22" font-weight="500" fill="rgba(255,255,255,0.72)">
      powered by Elevate
    </text>
  </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

// ✅ idle callback helper (safe fallback)
const ric =
  typeof window !== "undefined" && window.requestIdleCallback
    ? window.requestIdleCallback.bind(window)
    : (cb, opts) => {
        const t = setTimeout(
          () => cb({ didTimeout: true, timeRemaining: () => 0 }),
          (opts && opts.timeout) || 120
        );
        return t;
      };

const cancelRic =
  typeof window !== "undefined" && window.cancelIdleCallback
    ? window.cancelIdleCallback.bind(window)
    : (id) => clearTimeout(id);

// ✅ fast auth-settled helper (fixes "delete works after refresh" without slowing app)
const ensureAuthReadyFast = async (ensureAnonUserFn, authObj, maxMs = 600) => {
  const cu = authObj.currentUser || (await ensureAnonUserFn({ waitMs: 0 }));

  try {
    await cu.getIdToken(true);
  } catch {}

  if (authObj.currentUser?.uid === cu.uid) return cu;

  const start = Date.now();
  while (Date.now() - start < maxMs) {
    if (authObj.currentUser?.uid === cu.uid) return authObj.currentUser;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 40));
  }

  return cu;
};

const EVENT_DATA_CACHE_PREFIX = "elevate:eventData:";

const getCachedEventData = (key) => {
  try {
    const raw = sessionStorage.getItem(`${EVENT_DATA_CACHE_PREFIX}${key}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setCachedEventData = (key, value) => {
  try {
    if (key && value) {
      sessionStorage.setItem(`${EVENT_DATA_CACHE_PREFIX}${key}`, JSON.stringify(value));
    }
  } catch {}
};

const EventPage = () => {

  const [user, setUser] = useState(null);
  const fileInputRef = useRef(null);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
    if (!user) {
      ensureAnonUser()
        .then((cu) => cu?.getIdToken?.(true).catch(() => {}) || cu)
        .then((cu) => cu && setUser(cu))
        .catch(() => {});
    }
  }, [user]);

  const [storybookOpen, setStorybookOpen] = useState(false);
  const [storybookShowFaq, setStorybookShowFaq] = useState(false);

 

  const [bypassGate, setBypassGate] = useState(false);

  const [sbQty] = useState(1);
  const [sbPhotos] = useState(60);
  const [sbCheckoutLoading, setSbCheckoutLoading] = useState(false);

  const [eventDocId, setEventDocId] = useState(null);

  const handleStorybookCheckout = async () => {
    if (sbCheckoutLoading) return;
    try {
      if (!eventDocId) {
        alert("Event not loaded yet. Please try again.");
        return;
      }
      setSbCheckoutLoading(true);
      const base = window.location.origin;
      const successUrl = `${base}/event/${uniqueUrl}?storybook=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${base}/event/${uniqueUrl}?storybook=cancel`;

      await startStorybookCheckout({
        eventId: eventDocId,
        successUrl,
        cancelUrl,
        quantity: Math.max(1, Number(sbQty) || 1),
        photoCount: Math.max(1, Number(sbPhotos) || 60),
      });
    } catch (e) {
      console.error(e);
      alert("Could not start checkout. Please try again.");
    } finally {
      setSbCheckoutLoading(false);
    }
  };

  const [pageLoaded, setPageLoaded] = useState(false);
const [galleryLoadError, setGalleryLoadError] = useState(false);
const [galleryRetryKey, setGalleryRetryKey] = useState(0);
 
  

  const [emailGateOpen, setEmailGateOpen] = useState(false);
  const [pendingDownloadUrl, setPendingDownloadUrl] = useState(null);
  const [pendingFileName, setPendingFileName] = useState("");

  const routeParams = useParams();
  let uniqueUrl = routeParams.uniqueUrl;

  if (!uniqueUrl && typeof window !== "undefined") {
    const parts = window.location.pathname.split("/").filter(Boolean);
    const idxSingular = parts.indexOf("event");
    const idxPlural = parts.indexOf("events");
    const baseIdx = idxSingular !== -1 ? idxSingular : idxPlural;
    if (baseIdx !== -1 && parts[baseIdx + 1]) uniqueUrl = parts[baseIdx + 1];
  }



const [eventData, setEventData] = useState(() => {
  const key =
    uniqueUrl ||
    (typeof window !== "undefined"
      ? window.location.pathname.split("/").filter(Boolean).pop()
      : "");

  return getCachedEventData(key);
});
const [eventLoading, setEventLoading] = useState(true);
const [eventError, setEventError] = useState(null);
const [eventRetryKey, setEventRetryKey] = useState(0);

// Once the event has loaded successfully, do not let a late cold-start
// timeout/not-found/listener hiccup erase the page.
const eventHasLoadedRef = useRef(false);
useEffect(() => {
  eventHasLoadedRef.current = false;
}, [uniqueUrl]);

const retryEventLoad = useCallback(() => {
  eventHasLoadedRef.current = false;
  setEventError(null);
  setEventLoading(true);
  setEventDocId(null);
  setEventRetryKey((current) => current + 1);
}, []);

useEffect(() => {
  if (!eventData) return;

  setEventLoading(false);
  setEventError(null);
}, [eventData]);
  // ✅ NEW: hamburger menu open state (lazy loaded drawer)
  const [menuOpen, setMenuOpen] = useState(false);
const [templatesOpen, setTemplatesOpen] = useState(false);

 useEffect(() => {
  let cancelled = false;

  if (!eventData) {
  setEventLoading(true);
}

  setEventError(null);

  const resolveEvent = async () => {
    if (!uniqueUrl) {
      if (cancelled) return;

      if (!eventHasLoadedRef.current) {
        setEventDocId(null);
        setEventData(null);
        setEventError("not-found");
        setEventLoading(false);
      }

      return;
    }

    try {
      const resolvedId = await withOneSilentRetry(
        () => resolveEventDocIdFromSlug(uniqueUrl),
        "Event resolve"
      );

      if (cancelled) return;

if (resolvedId) {
  setEventDocId(resolvedId);
  setEventError(null);
  return;
}
      // The requests completed correctly, but the event did not exist.
      setEventDocId(null);
      setEventData(null);
      setEventError("not-found");
      setEventLoading(false);
    } catch (error) {
      if (cancelled || eventHasLoadedRef.current) return;

      console.error(
        "Event resolution failed after silent retry:",
        error
      );

      setEventDocId(null);
      setEventData(null);
      setEventError("load-failed");
      setEventLoading(false);
    }
  };

  resolveEvent();

  return () => {
    cancelled = true;
  };
}, [uniqueUrl, eventRetryKey]);

  const [photoCountServer, setPhotoCountServer] = useState(null);
  const fetchPhotoCount = useCallback(async () => {
    if (!eventDocId) return;
    try {
      const mediaRef = collection(db, "events", eventDocId, "media");
      const q = query(mediaRef, where("type", "==", "photo"));
      const snap = await getCountFromServer(q);
      setPhotoCountServer(snap.data().count || 0);
    } catch (e) {
      console.warn("getCountFromServer failed", e);
    }
  }, [eventDocId]);

  useEffect(() => {
    if (storybookOpen) fetchPhotoCount();
  }, [storybookOpen, fetchPhotoCount]);

  const [videoBlockedMessage, setVideoBlockedMessage] = useState("");
  const [media, setMedia] = useState([]);

  const optimisticBoomerangUrlsRef = useRef(new Map());

const releaseOptimisticBoomerangUrls = useCallback((mediaId) => {
  if (!mediaId) return;

  const urls = optimisticBoomerangUrlsRef.current.get(mediaId);
  if (!urls) return;

  for (const url of urls) {
    try {
      URL.revokeObjectURL(url);
    } catch {}
  }

  optimisticBoomerangUrlsRef.current.delete(mediaId);
}, []);

  // ✅ Patch a media item already in state (for optimistic boomerang playback)
  const patchMediaItem = useCallback((id, patch) => {
    setMedia((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }, []);

  // ✅ Ensure media item exists (so patch works even before snapshot adds it)
  const ensureMediaItemInState = useCallback((id, baseItem) => {
    setMedia((prev) => {
      if (prev.some((m) => m.id === id)) return prev;
      return [baseItem, ...prev];
    });
  }, []);

  useEffect(
  () => () => {
    for (const urls of optimisticBoomerangUrlsRef.current.values()) {
      for (const url of urls) {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      }
    }

    optimisticBoomerangUrlsRef.current.clear();
  },
  []
);

  const [lastDoc, setLastDoc] = useState(null);
const [hasMore, setHasMore] = useState(true);
const [isLoadingPage, setIsLoadingPage] = useState(false);
const [paginationError, setPaginationError] = useState(false);

const paginationInitializedRef = useRef(false);
const paginationInFlightRef = useRef(false);
  
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [openPhotoViewer, setOpenPhotoViewer] = useState(false);
  const [index, setIndex] = useState(0);
  const [viewerActiveId, setViewerActiveId] = useState(null);
  const [loading, setLoading] = useState(true);

 const [isUploadingBatch, setIsUploadingBatch] = useState(false);
const [uploadProgress, setUploadProgress] = useState(0);
const [completedUploadsCount, setCompletedUploadsCount] = useState(0);
const [totalUploadsCount, setTotalUploadsCount] = useState(0);
const [uploadWarningMessage, setUploadWarningMessage] = useState("");
const [showUploadSuccessMessage, setShowUploadSuccessMessage] = useState(false);

const [exportOpen, setExportOpen] = useState(false);

  const [selectedVibe, setSelectedVibe] = useState("");
  const [uploaderName, setUploaderName] = useState("");

  const [filterVibe, setFilterVibe] = useState("all");
  const deferredFilter = useDeferredValue(filterVibe);

  const [detailsOpen, setDetailsOpen] = useState(false);
const [stagedFiles, setStagedFiles] = useState([]);
const [isStartingUpload, setIsStartingUpload] = useState(false);

  useEffect(() => {
    if (!videoBlockedMessage) return;
    const t = setTimeout(() => setVideoBlockedMessage(""), 4500);
    return () => clearTimeout(t);
  }, [videoBlockedMessage]);

  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const uploadFileProgressRef = useRef(new Map());
  const totalBytesToUploadRef = useRef(0);

  // Ensure auth
  // Ensure auth, but DO NOT let auth keep the public event page stuck loading
// Auth should not compete with cold event loading.
// Normal public events can wait; upload/delete still forces auth when needed.
useEffect(() => {
  let cancelled = false;

  // Do not let auth control the public page loading state.
  setLoading(false);

  const unsub = onAuthStateChanged(auth, (cu) => {
    if (!cancelled) {
      setUser(cu || null);
    }
  });

  // Warm up anon auth after the event page gets a chance to load.
  const delayedAnon = setTimeout(() => {
    if (cancelled) return;

    ensureAnonUser({ waitMs: 0 })
      .then((cu) => {
        if (!cancelled && cu) setUser(cu);
      })
      .catch((e) => {
        console.warn("Delayed anon auth failed, continuing:", e);
      });
  }, 3500);

  return () => {
    cancelled = true;
    clearTimeout(delayedAnon);
    unsub();
  };
}, []);


// Hidden/strict galleries need a user identity quickly,
// otherwise the app cannot know which uploads belong to this guest.
useEffect(() => {
  if (!eventData) return;
  if (user) return;

  const needsUserIdentity =
    eventData?.galleryVisibleToGuests === false ||
    !!(eventData?.strictSurprise ?? eventData?.customization?.strictSurprise);

  if (!needsUserIdentity) return;

  let cancelled = false;

  ensureAnonUser({ waitMs: 0 })
    .then((cu) => {
      if (!cancelled && cu) setUser(cu);
    })
    .catch((e) => {
      console.warn("Anon auth for hidden/strict gallery failed:", e);
    });

  return () => {
    cancelled = true;
  };
}, [eventData, user]);



  

  useEffect(() => {
  if (!eventDocId) return undefined;

  let cancelled = false;
  let unsubscribe = null;
  let didLoadEvent = false;

  if (!eventData) {
  setEventLoading(true);
}

  setEventError(null);

  const eventRef = doc(db, "events", eventDocId);

  const applyEventSnap = (eventSnap) => {
    if (cancelled) return;

    if (eventSnap.exists()) {
      didLoadEvent = true;
      eventHasLoadedRef.current = true;

      const data = eventSnap.data();

      setEventData(data);
      setCachedEventData(eventDocId || uniqueUrl, data);
      setBackgroundColor(
        data?.customization?.backgroundColor || "#ffffff"
      );

      setEventError(null);
      setEventLoading(false);

      try {
        const __pid = resolvePlanPriceId(data);
        console.log(
          "[EP] resolved pid:",
          __pid,
          "allowed:",
          getAllowedGuests(data)
        );
      } catch {}

      return;
    }

    if (!eventHasLoadedRef.current) {
      setEventData(null);
      setEventError("not-found");
      setEventLoading(false);
    }

    console.log("❌ Event not found in Firestore");
  };

  const loadEvent = async () => {
    try {
      const eventSnap = await withOneSilentRetry(
        () => getDoc(eventRef),
        "Event document load"
      );

      if (cancelled || didLoadEvent) return;

      applyEventSnap(eventSnap);
    } catch (error) {
      if (
        cancelled ||
        didLoadEvent ||
        eventHasLoadedRef.current
      ) {
        return;
      }

      console.error(
        "Event document failed after silent retry:",
        error
      );

      setEventError("load-failed");
      setEventLoading(false);
    }
  };

  loadEvent();

  // Keep realtime updates, but do not let a temporary listener error
  // override a successful page or prematurely show the error screen.
  unsubscribe = onSnapshot(
    eventRef,
    (eventSnap) => {
      applyEventSnap(eventSnap);
    },
    (error) => {
      if (cancelled) return;

      console.warn(
        "Realtime event listener temporarily failed:",
        error
      );
    }
  );

  return () => {
    cancelled = true;

    if (unsubscribe) {
      unsubscribe();
    }
  };
}, [eventDocId, uniqueUrl]);

  

useEffect(() => {
  setMedia([]);
  setLastDoc(null);
  setHasMore(true);
  setIsLoadingPage(false);
  setPaginationError(false);
  setGalleryLoadError(false);

  paginationInitializedRef.current = false;
  paginationInFlightRef.current = false;

  setPageLoaded(false);
  setBypassGate(false);
  setMenuOpen(false);
  setTemplatesOpen(false);

}, [eventDocId]);


useEffect(() => {
  if (!eventDocId) return undefined;

  let cancelled = false;

  const mediaRef = collection(
    db,
    "events",
    eventDocId,
    "media"
  );

  const liveQuery = query(
    mediaRef,
    orderBy("timestamp", "desc"),
    fbLimit(PAGE_SIZE)
  );

  const applyMediaSnapshot = (snapshot) => {
    if (cancelled) return;

    const freshItems = snapshot.docs
      .map((mediaDoc) => ({
        id: mediaDoc.id,
        ...mediaDoc.data(),
      }))
      .filter(
        (item) =>
          isRenderablePhoto(item) ||
          isRenderableVideo(item)
      );

    setMedia((previousItems) => {
      const previousById = new Map(
        previousItems.map((item) => [item.id, item])
      );

      const mergedFreshItems = freshItems.map((freshItem) => {
  const previousItem = previousById.get(freshItem.id);

  const remoteBoomerangReady =
    freshItem.boomerangStatus === "ready" &&
    isHttpish(freshItem.boomerangUrl);

  return {
    ...previousItem,
    ...freshItem,

    // Keep playing the local recording until the permanent MP4 is ready.
   boomerangLocalUrl:
  previousItem?.boomerangLocalUrl || null,

    isOptimistic: remoteBoomerangReady
      ? false
      : previousItem?.isOptimistic || false,
  };
});



      const freshIds = new Set(
        mergedFreshItems.map((item) => item.id)
      );

      const olderItems = previousItems.filter(
        (item) => !freshIds.has(item.id)
      );

      return [...mergedFreshItems, ...olderItems];
    });

   const serverConfirmed = !snapshot.metadata?.fromCache;
const hasSnapshotDocs = snapshot.docs.length > 0;

if (
  !paginationInitializedRef.current &&
  (hasSnapshotDocs || serverConfirmed)
) {
  setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
  setHasMore(snapshot.docs.length === PAGE_SIZE);
  paginationInitializedRef.current = true;
}

// Receiving any valid snapshot means the skeleton can stop.
// A later server snapshot will still populate the existing photos.
setGalleryLoadError(false);
setPageLoaded(true);
  };

  const recoverInitialGallery = async () => {
    try {
      const snapshot = await withOneSilentRetry(
        () => getDocs(liveQuery),
        "Initial gallery load"
      );

      applyMediaSnapshot(snapshot);
    } catch (error) {
      if (cancelled) return;

      console.error(
        "Initial gallery load failed after retry:",
        error
      );

      setGalleryLoadError(true);
      setPageLoaded(false);
    }
  };

  const unsubscribe = onSnapshot(
  liveQuery,
  { includeMetadataChanges: true },
  applyMediaSnapshot,
  (error) => {
      if (cancelled) return;

      console.error(
        "Live media snapshot error:",
        error
      );

      recoverInitialGallery();
    }
  );

  return () => {
    cancelled = true;
    unsubscribe();
  };
}, [
  eventDocId,
  galleryRetryKey,
  releaseOptimisticBoomerangUrls,
]);




const fetchPage = useCallback(
  async (manualRetry = false) => {
    if (
  !eventDocId ||
  !paginationInitializedRef.current ||
  paginationInFlightRef.current ||
  !hasMore
) {
  return;
}

    if (paginationError && !manualRetry) return;

    paginationInFlightRef.current = true;
    setIsLoadingPage(true);
    setPaginationError(false);

    try {
      const mediaRef = collection(
        db,
        "events",
        eventDocId,
        "media"
      );

      const base = query(
        mediaRef,
        orderBy("timestamp", "desc")
      );

      const q2 = lastDoc
        ? query(
            base,
            startAfter(lastDoc),
            fbLimit(PAGE_SIZE)
          )
        : query(
            base,
            fbLimit(PAGE_SIZE)
          );

     let snap;

try {
  snap = await getDocs(q2);
} catch (firstError) {
  console.warn(
    "Gallery pagination first attempt failed. Retrying...",
    firstError
  );

  const retryDelay = 800 + Math.random() * 700;
  await wait(retryDelay);

  snap = await getDocs(q2);
}

      const docs = snap.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
        }))
        .filter(
          (x) =>
            isRenderablePhoto(x) ||
            isRenderableVideo(x)
        );

      setMedia((prev) => {
        const existingIds = new Set(
          prev.map((x) => x.id)
        );

        const existingUrls = new Set(
          prev.map((x) => x.url).filter(Boolean)
        );

        const toAdd = docs.filter(
          (d) =>
            !existingIds.has(d.id) &&
            !(d.url && existingUrls.has(d.url))
        );

        return [...prev, ...toAdd];
      });

      const newLast =
        snap.docs[snap.docs.length - 1] || null;

      setLastDoc(newLast);
setHasMore(snap.docs.length === PAGE_SIZE);
setGalleryLoadError(false);
setPageLoaded(true);
    } catch (err) {
      console.error(
        "Pagination fetch error:",
        err
      );

      setPaginationError(true);
    } finally {
      paginationInFlightRef.current = false;
      setIsLoadingPage(false);
     
      
    }
  },
  [
    eventDocId,
    hasMore,
    lastDoc,
    paginationError,
  ]
);




  // ✅ More aggressive prefetch on scroll
  useEffect(() => {
    const onScroll = () => {
      if (!hasMore || isLoadingPage) return;
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const denom = Math.max(1, scrollHeight - clientHeight);
      const ratio = scrollTop / denom;
      if (ratio > PREFETCH_SCROLL_RATIO) fetchPage();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [hasMore, isLoadingPage, fetchPage]);

  // ✅ IntersectionObserver sentinel prefetch (earlier than visible)
  const prefetchSentinelRef = useRef(null);
  useEffect(() => {
    const el = prefetchSentinelRef.current;
    if (!el) return;
    if (!hasMore) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first) return;
        if (first.isIntersecting) {
          fetchPage();
        }
      },
      { root: null, rootMargin: PREFETCH_IO_ROOT_MARGIN, threshold: 0.01 }
    );

    obs.observe(el);
    return () => {
      try {
        obs.unobserve(el);
      } catch {}
      obs.disconnect();
    };
  }, [hasMore, fetchPage]);

 

  useEffect(() => {
    if (!user || media.length === 0) {
      setUploadWarningMessage("");
      return;
    }
    const now = Date.now();
    const oneMinuteAgo = now - 60 * 1000;
    const userUploadsLastMinute = media.filter((item) => {
      if (!item.timestamp || !item.userId) return false;
      let itemTimestamp;
      try {
        itemTimestamp = item.timestamp.toDate
          ? item.timestamp.toDate().getTime()
          : new Date(item.timestamp).getTime();
        if (isNaN(itemTimestamp)) return false;
      } catch (e) {
        console.error("Error processing timestamp:", item.timestamp, e);
        return false;
      }
      return item.userId === user.uid && itemTimestamp > oneMinuteAgo;
    });
    if (userUploadsLastMinute.length >= 100) {
      setUploadWarningMessage("High upload volume detected. Please upload in smaller batches or delete some items.");
    } else {
      setUploadWarningMessage("");
    }
  }, [media, user]);

  useEffect(() => {
    let timer;
    if (showUploadSuccessMessage) {
      timer = setTimeout(() => setShowUploadSuccessMessage(false), 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showUploadSuccessMessage]);

  const planner = !!(user && eventData && user.uid === eventData.userId);

  const computedEndUTC = useMemo(() => {
    const raw = eventData?.details?.eventDate;
    return computeEndMsFromEventDateLocal(raw);
  }, [eventData?.details?.eventDate]);

  const eventForAccess = useMemo(() => {
    if (!eventData) return eventData;
    return computedEndUTC ? { ...eventData, endUTC: computedEndUTC } : eventData;
  }, [eventData, computedEndUTC]);

  const galleryState = useMemo(() => {
    const uid = user?.uid || null;
    const contributedByMedia = !!uid && media.some((m) => m.userId === uid || m.uploaderUid === uid);
    const contributedByList =
      !!uid && Array.isArray(eventData?.guestContributors) && eventData.guestContributors.includes(uid);
    const _viewer = { hasContributed: contributedByMedia || contributedByList };

   return getGalleryState({
  event: eventForAccess,
  viewer: _viewer,
  userId: planner ? null : uid,
  nowUtcMs: Date.now(),
});
 }, [eventForAccess, media, user?.uid, planner, eventData?.guestContributors]);

  const forceStrict = galleryState === "STRICT_SELF_VIEW";
  const revealAtMs = computedEndUTC || null;

  const hasStrict = !!(eventData?.strictSurprise ?? eventData?.customization?.strictSurprise);

  const showRevealBanner = hasStrict && forceStrict;

const galleryHiddenByHost =
  eventData?.galleryVisibleToGuests === false;

// Once a strict surprise is over, reveal every photo to everyone.
// Normal manually hidden galleries remain unchanged.
const strictSurpriseEnded =
  hasStrict && galleryState === "POST_UNLOCKED";

const effectiveGalleryHiddenForGuests =
  galleryHiddenByHost && !strictSurpriseEnded;

const manualGalleryHiddenForGuests =
  effectiveGalleryHiddenForGuests && !planner;
const baseVisibleMedia = useMemo(() => {
  const displayableMedia = planner
    ? media
    : media.filter((m) => m.hidden !== true);

  if (manualGalleryHiddenForGuests) {
    const uid = user?.uid;
    if (!uid) return [];

    return displayableMedia.filter((m) => {
      const isMine =
        m.userId === uid || m.uploaderUid === uid;

      const isLocalUpload =
        String(m.id || "").startsWith("local-") &&
        Boolean(m.url) &&
        m.userId === uid;

      return isMine || isLocalUpload;
    });
  }

  return displayableMedia;
}, [
  media,
  planner,
  manualGalleryHiddenForGuests,
  user?.uid,
]);

 const visibleMedia = useMemo(() => {
  // The host always sees every photo.
  if (planner || !forceStrict) {
    return baseVisibleMedia;
  }

  const uid = user?.uid;
  if (!uid) return [];

  return baseVisibleMedia.filter(
    (m) => m.userId === uid || m.uploaderUid === uid
  );
}, [
  baseVisibleMedia,
  forceStrict,
  planner,
  user?.uid,
]);

  const filteredMedia = useMemo(
    () => visibleMedia.filter((item) => deferredFilter === "all" || item.vibe === deferredFilter),
    [visibleMedia, deferredFilter]
  );

  const gridPhotos = useMemo(() => filteredMedia.filter((item) => isRenderablePhoto(item)), [filteredMedia]);

  
  const liveCoverSrc = React.useMemo(() => {
    if (!gridPhotos.length) return "";
    const first = gridPhotos[0];
    return isHttpish(first.url) ? first.url : first.thumbnailUrl || "";
  }, [gridPhotos]);

  const hasAnyPhotos = filteredMedia.some((m) => isRenderablePhoto(m));

  const countSourcePhotos = useMemo(
    () => baseVisibleMedia.filter((item) => isRenderablePhoto(item)),
    [baseVisibleMedia]
  );

  const photosForViewer = useMemo(
    () =>
      gridPhotos.map((item) => {
        const stillUrl = isHttpish(item.url) ? item.url : item.thumbnailUrl;

        const localOk = isHttpish(item.boomerangLocalUrl) || (typeof item.boomerangLocalUrl === "string" && item.boomerangLocalUrl.startsWith("blob:"));
        const remotePlayable =
  !!item.isBoomerang &&
  isHttpish(item.boomerangUrl) &&
  item.boomerangStatus !== "error";

       const boomerangPlayable = localOk || remotePlayable;

        return {
          ...item,
          viewerStillUrl: stillUrl,
          isBoomerang: boomerangPlayable,
          boomerangUrl: remotePlayable ? item.boomerangUrl : null,
          boomerangLocalUrl: localOk ? item.boomerangLocalUrl : null,
        };
      }),
    [gridPhotos]
  );

  useEffect(() => {
    if (!openPhotoViewer) return;
    if (!viewerActiveId) return;
    const idx = photosForViewer.findIndex((p) => p.id === viewerActiveId);
    if (idx === -1) return;
    setIndex((prev) => (idx !== prev ? idx : prev));
  }, [photosForViewer, openPhotoViewer, viewerActiveId]);

  useEffect(() => {
    if (!openPhotoViewer) return;
    if (!photosForViewer.length) return;
    if (index > photosForViewer.length - 1) setIndex(photosForViewer.length - 1);
  }, [openPhotoViewer, photosForViewer.length, index]);

  // --- Storybook progress state ---
  const storybookCurrentRaw = photoCountServer ?? gridPhotos.length;
  const storybookTarget = 50;
  const storybookDisplayCurrent = Math.min(storybookCurrentRaw, storybookTarget);
  const storybookPct = Math.max(
    0,
    Math.min(100, Math.round((storybookDisplayCurrent / Math.max(1, storybookTarget)) * 100))
  );
  const storybookLabelCurrent =
    storybookCurrentRaw > storybookTarget ? `${storybookTarget}+` : String(storybookCurrentRaw);
  const storybookLocked = storybookCurrentRaw < storybookTarget;

  const viewer = useMemo(() => {
    const uid = user?.uid;
    if (!uid) return { hasContributed: false };
    const contributedByMedia = media.some((m) => m.userId === uid || m.uploaderUid === uid);
    const contributedByList =
      Array.isArray(eventData?.guestContributors) && eventData.guestContributors.includes(uid);
    return { hasContributed: contributedByMedia || contributedByList };
  }, [media, user?.uid, eventData?.guestContributors]);

  const showLockedUI = useMemo(() => {
  // The host never receives the guest gate.
  // Only skip the host's cover/access screen in strict Surprise Mode.
if (planner && hasStrict) return false;

  const hasContrib = !!viewer?.hasContributed;
  const isPostEvent = galleryState === "POST_UNLOCKED";

  if (isPostEvent) return false;

  if (hasStrict) {
    if (forceStrict) return !hasContrib;
    return false;
  }

  return !hasContrib;
}, [
  planner,
  viewer?.hasContributed,
  hasStrict,
  forceStrict,
  galleryState,
]);

  useEffect(() => {
    if (eventData?.strictSurprise || eventData?.customization?.strictSurprise) {
      if (galleryState === "DURING_UNLOCKED") {
        setShowUploadSuccessMessage(true);
        window.scrollTo({ top: window.innerHeight * 0.9, behavior: "smooth" });
      }
    }
  }, [galleryState, eventData]);

 
  const scrollRef = useRef(null);
  const [showVibeArrow, setShowVibeArrow] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      const hasOverflow = el.scrollWidth > el.clientWidth + 2;
      const atEnd = Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth - 1;
      setShowVibeArrow(hasOverflow && !atEnd);
    };

    update();
    const onScroll = () => update();
    const onResize = () => update();

    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    const t = setTimeout(update, 50);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      clearTimeout(t);
    };
  }, [filterVibe, visibleMedia]);

  const calculateTotalTransferred = () => {
    let total = 0;
    uploadFileProgressRef.current.forEach((b) => (total += b));
    return total;
  };

  const runWithConcurrency = async (items, limit, worker) => {
  const results = [];
  let nextIndex = 0;

  const runners = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (nextIndex < items.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;

        try {
          results[currentIndex] = await worker(items[currentIndex], currentIndex);
        } catch (error) {
          results[currentIndex] = {
            status: "error",
            name: items[currentIndex]?.name || "photo",
            error: error.message || "Upload failed",
          };
        }
      }
    }
  );

  await Promise.all(runners);
  return results;
};

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const { ok: stage1Files, errs } = await validateInitial(files);

    if (errs.length) {
      alert(
        `The following files couldn't be uploaded:\n\n${errs.join(
          "\n"
        )}\n\nThis event is collecting photos only.`
      );
    }

    e.target.value = "";
    if (stage1Files.length === 0) return;

    setStagedFiles(stage1Files);
    setDetailsOpen(true);
  };

 const beginBatchUpload = async (filesToUpload = []) => {
    if (isUploadingBatch || isStartingUpload) return;

    if (!eventDocId) {
      alert("Event is still loading. Please try again in a second.");
      return;
    }

    if (!filesToUpload.length) {
      alert("No photos selected. Please choose photos again.");
      setDetailsOpen(false);
      setStagedFiles([]);
      return;
    }

    setIsStartingUpload(true);

    let currentUser;

    try {
      // ✅ ensure auth is truly settled before Storage uploads
      currentUser = await ensureAuthReadyFast(ensureAnonUser, auth, 1500);

      if (!currentUser?.uid) {
        throw new Error("Could not start guest session.");
      }

      setUser(currentUser);
      await currentUser.getIdToken(true); // forces token refresh
    } catch (err) {
      console.error("Could not prepare upload auth:", err);
      setIsStartingUpload(false);
      alert("We couldn’t start your upload yet. Please tap Upload again.");
      return;
    }

    const uid = currentUser.uid;
    setIsStartingUpload(false);

    const isPlanner = eventData && uid === eventData.userId;

    if (!isPlanner) {
      const guestContributors = eventData.guestContributors || [];
      const allowedGuests = getAllowedGuests(eventData);
      if (!guestContributors.includes(uid)) {
        if (guestContributors.length >= allowedGuests) {
          alert(
            `Guest contribution limit reached (${allowedGuests} guests allowed). You cannot upload photos/videos as a new contributor.`
          );
          setDetailsOpen(false);
          setStagedFiles([]);
          return;
        }
      }
    }

   setIsUploadingBatch(true);
setCompletedUploadsCount(0);
setTotalUploadsCount(filesToUpload.length);
setUploadProgress(0);
uploadFileProgressRef.current = new Map();

// Close the name/vibe modal immediately after upload starts.
// The top pill will show upload progress instead.
setDetailsOpen(false);
setStagedFiles([]);

totalBytesToUploadRef.current = filesToUpload.reduce((sum, f) => sum + f.size, 0);
    if (totalBytesToUploadRef.current === 0 && filesToUpload.length > 0) totalBytesToUploadRef.current = 1;

  const uploadOneFile = async (file) => {
      try {
        const sourceBlob = await cropAndResizeImage(file, UPLOAD_MAX_SIDE, MAX_PHOTO_MB);
        const thumbBlob = await cropAndResizeImage(file, THUMB_MAX_SIDE, THUMB_MAX_MB);

       const safeBase = (file.name || "photo.jpg")
  .replace(/\s+/g, "_")
  .replace(/[^\w.\-]/g, "_")
  .replace(/\.(png|gif|bmp|webp)$/i, ".jpg");

const thumbPath = `events/${eventDocId}/${uid}/${Date.now()}_thumb_${safeBase}`;

const [photoUrl, thumbUrl] = await Promise.all([
  uploadMedia(eventDocId, sourceBlob, {
    uid,
    onProgress: (snapshot) => {
      uploadFileProgressRef.current.set(file.name + file.size, snapshot.bytesTransferred);
      const currentTotalTransferred = calculateTotalTransferred();
      const progress =
        totalBytesToUploadRef.current > 0
          ? (currentTotalTransferred / totalBytesToUploadRef.current) * 100
          : 0;
      setUploadProgress(Math.min(progress, 100));
    },
    onComplete: () => uploadFileProgressRef.current.delete(file.name + file.size),
    onError: (error) => {
      console.error(`Upload failed for ${file.name}:`, error);
      uploadFileProgressRef.current.delete(file.name + file.size);
    },
  }),
  uploadMedia(eventDocId, thumbBlob, {
    uid,
    storagePath: thumbPath,
  }),
]);
        const mediaRef = collection(db, "events", eventDocId, "media");
        const newDocRef = await addDoc(mediaRef, {
          url: photoUrl,
          thumbnailUrl: thumbUrl,
          type: "photo",
          isBoomerang: false,
          boomerangUrl: null,
          timestamp: serverTimestamp(),
          uploaderUid: uid,
          eventId: eventDocId,
          userId: uid,
          vibe: selectedVibe || null,
          uploaderName: uploaderName || null,
          imagePipeline: "v2_source+thumb",
        });

        

        setCompletedUploadsCount((prev) => prev + 1);
        return { status: "success", name: file.name, url: photoUrl, type: "photo", thumbUrl };
      } catch (error) {
        console.error(`Error processing or uploading ${file.name}:`, error);
        uploadFileProgressRef.current.delete(file.name + file.size);
        return { status: "error", name: file.name, error: error.message };
      }
   };

    try {
    const results = await runWithConcurrency(filesToUpload, 4, uploadOneFile);
      const anySuccessfulUploads = results.some((r) => r.status === "success");
   if (anySuccessfulUploads) setShowUploadSuccessMessage(true);

      if (!isPlanner) {
        const guestContributors = eventData.guestContributors || [];
        if (uid && !guestContributors.includes(uid)) {
          try {
            const eventRef = doc(db, "events", eventDocId);
           await updateDoc(eventRef, { guestContributors: [...guestContributors, uid] });
setEventData((prev) => ({
  ...prev,
  guestContributors: [...(prev?.guestContributors || []), uid],
}));
          } catch (e) {
            console.warn("guestContributors update skipped for guest:", e);
          }
        }
      }

     const added = results
  .filter((r) => r.status === "success")
  .map((r) => ({
    id: `local-${Math.random().toString(36).slice(2)}`,
    url: r.url,
    thumbnailUrl: r.thumbUrl || r.url || null,
    type: r.type,
    isBoomerang: false,
    boomerangUrl: null,
    timestamp: new Date(),
    userId: uid,
    uploaderUid: uid,
    vibe: selectedVibe,
    uploaderName,
  }));

      if (added.length) {
        setMedia((prev) => {
          const existingUrls = new Set(prev.map((m) => m.url));
          const toAdd = added.filter((a) => !existingUrls.has(a.url));
          return toAdd.length ? [...toAdd, ...prev] : prev;
        });
      }

     const failed = results.filter((r) => r.status !== "success");
if (failed.length > 0) {
  const successCount = results.filter((r) => r.status === "success").length;

  if (successCount > 0) {
    alert(
      `${successCount} photo${successCount === 1 ? "" : "s"} saved successfully. ` +
      `${failed.length} photo${failed.length === 1 ? "" : "s"} couldn't save. ` +
      `Please try uploading the remaining photo${failed.length === 1 ? "" : "s"} again.`
    );
  } else {
    alert("We couldn’t save your photos. Please check your connection and try again.");
  }
}
    } catch (err) {
      console.error("Unexpected batch error:", err);
    } finally {
      setIsUploadingBatch(false);
      setUploadProgress(0);
      setCompletedUploadsCount(0);
      setTotalUploadsCount(0);
      uploadFileProgressRef.current = new Map();
      totalBytesToUploadRef.current = 0;
      setDetailsOpen(false);
      setStagedFiles([]);
    }
  };

  const handleCameraUpload = async (
  payloadOrBlob,
  uploaderNameArg,
  selectedVibeArg
) => {
  if (!eventDocId) {
    throw new Error("Missing eventDocId");
  }

  const isBlob =
    payloadOrBlob instanceof Blob ||
    (payloadOrBlob &&
      typeof payloadOrBlob === "object" &&
      typeof payloadOrBlob.size === "number" &&
      typeof payloadOrBlob.type === "string");

  const boomerangPayload = isBlob
    ? null
    : payloadOrBlob || {};

  const isOptimisticBoomerang =
    boomerangPayload?.isBoomerang === true &&
    boomerangPayload?.heroBlob?.size > 0 &&
    boomerangPayload?.clipBlob?.size > 0;

  let optimisticBoomerang = null;

  /*
   * Immediately insert the phone's local recording into the gallery.
   * This runs before Firebase authentication, uploads or FFmpeg.
   */
  if (isOptimisticBoomerang) {
    const randomPart =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()
            .toString(36)
            .slice(2)}`;

    const temporaryId = `local-boomerang-${randomPart}`;

    const localHeroUrl = URL.createObjectURL(
      boomerangPayload.heroBlob
    );

    const localClipUrl = URL.createObjectURL(
      boomerangPayload.clipBlob
    );

    const optimisticUid =
      auth.currentUser?.uid || user?.uid || null;

    optimisticBoomerangUrlsRef.current.set(temporaryId, [
      localHeroUrl,
      localClipUrl,
    ]);

    optimisticBoomerang = {
      temporaryId,
      localHeroUrl,
      localClipUrl,
    };

    ensureMediaItemInState(temporaryId, {
      id: temporaryId,
      type: "photo",
      url: localHeroUrl,
      thumbnailUrl: localHeroUrl,
      isBoomerang: true,
      boomerangStatus: "uploading",
      boomerangUrl: null,
      boomerangLocalUrl: localClipUrl,
      boomerangClipPath: null,
      timestamp: new Date(),
      userId: optimisticUid,
      uploaderUid: optimisticUid,
      vibe:
        selectedVibeArg ||
        selectedVibe ||
        null,
      uploaderName:
        uploaderNameArg ||
        uploaderName ||
        null,
      eventId: eventDocId,
      isOptimistic: true,
    });
  }

  let currentUser;

  try {
    currentUser = await ensureAuthReadyFast(
      ensureAnonUser,
      auth,
      900
    );

    await currentUser.getIdToken(true);
  } catch (error) {
    if (optimisticBoomerang) {
      patchMediaItem(
        optimisticBoomerang.temporaryId,
        {
          boomerangStatus: "error",
          uploadError: true,
        }
      );
    }

    throw error;
  }

  setUser(currentUser);

  const uid = currentUser.uid;

  if (optimisticBoomerang) {
    patchMediaItem(
      optimisticBoomerang.temporaryId,
      {
        userId: uid,
        uploaderUid: uid,
      }
    );
  }

  const makeSafeBase = () =>
    `camera_${Date.now()}.jpg`;

  /*
   * Normal photo flow remains unchanged.
   */
  if (isBlob) {
    const blob = payloadOrBlob;
    const safeBase = makeSafeBase();

    const srcFile =
      blob instanceof File
        ? blob
        : new File([blob], safeBase, {
            type: blob.type || "image/jpeg",
          });

    const sourceBlob = await cropAndResizeImage(
      srcFile,
      UPLOAD_MAX_SIDE,
      MAX_PHOTO_MB
    );

    const thumbBlob = await cropAndResizeImage(
      srcFile,
      THUMB_MAX_SIDE,
      THUMB_MAX_MB
    );

    const photoUrl = await uploadMedia(
      eventDocId,
      sourceBlob,
      { uid }
    );

    const thumbPath =
      `events/${eventDocId}/${uid}/` +
      `${Date.now()}_thumb_${safeBase}`;

    const thumbUrl = await uploadMedia(
      eventDocId,
      thumbBlob,
      {
        uid,
        storagePath: thumbPath,
      }
    );

    await addDoc(
      collection(
        db,
        "events",
        eventDocId,
        "media"
      ),
      {
        type: "photo",
        url: photoUrl,
        thumbnailUrl: thumbUrl,
        isBoomerang: false,
        boomerangStatus: null,
        boomerangUrl: null,
        boomerangClipPath: null,
        timestamp: serverTimestamp(),
        userId: uid,
        uploaderUid: uid,
        vibe:
          selectedVibeArg ||
          selectedVibe ||
          null,
        uploaderName:
          uploaderNameArg ||
          uploaderName ||
          null,
        eventId: eventDocId,
      }
    );

    return;
  }

  /*
   * Boomerang flow.
   */
  const {
    heroBlob,
    isBoomerang = false,
    clipBlob,
    clipExt,
  } = payloadOrBlob || {};

  if (!heroBlob || !heroBlob.size) {
    throw new Error("Missing heroBlob");
  }

  const heroSafeBase = makeSafeBase();

  const heroFile =
    heroBlob instanceof File
      ? heroBlob
      : new File([heroBlob], heroSafeBase, {
          type: heroBlob.type || "image/jpeg",
        });

  const heroSourceBlob = await cropAndResizeImage(
    heroFile,
    UPLOAD_MAX_SIDE,
    MAX_PHOTO_MB
  );

  const heroThumbBlob = await cropAndResizeImage(
    heroFile,
    THUMB_MAX_SIDE,
    THUMB_MAX_MB
  );

  const thumbPath =
    `events/${eventDocId}/${uid}/thumbs/` +
    `${Date.now()}_boomerang_hero.jpg`;

  let photoUrl;
  let thumbUrl;

  try {
    /*
     * Upload the source and thumbnail together instead of
     * waiting for each upload sequentially.
     */
    [photoUrl, thumbUrl] = await Promise.all([
      uploadMedia(
        eventDocId,
        heroSourceBlob,
        { uid }
      ),
      uploadMedia(
        eventDocId,
        heroThumbBlob,
        {
          uid,
          storagePath: thumbPath,
        }
      ),
    ]);
  } catch (error) {
    if (optimisticBoomerang) {
      patchMediaItem(
        optimisticBoomerang.temporaryId,
        {
          boomerangStatus: "error",
          uploadError: true,
        }
      );
    }

    throw error;
  }

  let mediaDocRef;

  try {
    mediaDocRef = await addDoc(
      collection(
        db,
        "events",
        eventDocId,
        "media"
      ),
      {
        type: "photo",
        url: photoUrl,
        thumbnailUrl: thumbUrl,
        isBoomerang: !!isBoomerang,
        boomerangStatus: isBoomerang
          ? "processing"
          : null,
        boomerangUrl: null,
        boomerangClipPath: null,
        timestamp: serverTimestamp(),
        userId: uid,
        uploaderUid: uid,
        vibe:
          selectedVibeArg ||
          selectedVibe ||
          null,
        uploaderName:
          uploaderNameArg ||
          uploaderName ||
          null,
        eventId: eventDocId,
      }
    );
  } catch (error) {
    if (optimisticBoomerang) {
      patchMediaItem(
        optimisticBoomerang.temporaryId,
        {
          boomerangStatus: "error",
          uploadError: true,
        }
      );
    }

    throw error;
  }

  if (optimisticBoomerang) {
    /*
     * Transfer ownership of the local URLs from the temporary
     * id to the permanent Firestore id.
     */
    const localUrls =
      optimisticBoomerangUrlsRef.current.get(
        optimisticBoomerang.temporaryId
      );

    optimisticBoomerangUrlsRef.current.delete(
      optimisticBoomerang.temporaryId
    );

    if (localUrls) {
      optimisticBoomerangUrlsRef.current.set(
        mediaDocRef.id,
        localUrls
      );
    }

    /*
     * Replace both the temporary item and any Firestore item
     * that arrived early with one permanent gallery card.
     */
    setMedia((previousItems) => {
      const temporaryItem = previousItems.find(
        (item) =>
          item.id ===
          optimisticBoomerang.temporaryId
      );

      const serverItem = previousItems.find(
        (item) => item.id === mediaDocRef.id
      );

      const permanentItem = {
        ...temporaryItem,
        ...serverItem,
        id: mediaDocRef.id,
        type: "photo",
        url: photoUrl,
        thumbnailUrl: thumbUrl,
        isBoomerang: true,
        boomerangStatus: "processing",
        boomerangUrl:
          serverItem?.boomerangUrl || null,
        boomerangLocalUrl:
          optimisticBoomerang.localClipUrl,
        timestamp:
          temporaryItem?.timestamp ||
          new Date(),
        userId: uid,
        uploaderUid: uid,
        vibe:
          selectedVibeArg ||
          selectedVibe ||
          null,
        uploaderName:
          uploaderNameArg ||
          uploaderName ||
          null,
        eventId: eventDocId,
        isOptimistic: true,
      };

      const remainingItems =
        previousItems.filter(
          (item) =>
            item.id !==
              optimisticBoomerang.temporaryId &&
            item.id !== mediaDocRef.id
        );

      return [
        permanentItem,
        ...remainingItems,
      ];
    });
  } else {
    ensureMediaItemInState(mediaDocRef.id, {
      id: mediaDocRef.id,
      type: "photo",
      url: photoUrl,
      thumbnailUrl: thumbUrl,
      isBoomerang: !!isBoomerang,
      boomerangStatus: isBoomerang
        ? "processing"
        : null,
      boomerangUrl: null,
      boomerangLocalUrl: null,
      timestamp: new Date(),
      userId: uid,
      uploaderUid: uid,
      vibe:
        selectedVibeArg ||
        selectedVibe ||
        null,
      uploaderName:
        uploaderNameArg ||
        uploaderName ||
        null,
      eventId: eventDocId,
    });
  }

  if (!isBoomerang) return;

  /*
   * Preserve the hero photograph if recording did not
   * produce a valid motion clip.
   */
  if (!clipBlob || !clipBlob.size) {
    await updateDoc(
      doc(
        db,
        "events",
        eventDocId,
        "media",
        mediaDocRef.id
      ),
      {
        isBoomerang: false,
        boomerangStatus: null,
        boomerangUrl: null,
        boomerangClipPath: null,
      }
    );

    patchMediaItem(mediaDocRef.id, {
      isBoomerang: false,
      boomerangStatus: null,
      boomerangUrl: null,
      boomerangLocalUrl: null,
      isOptimistic: false,
    });

    releaseOptimisticBoomerangUrls(
      mediaDocRef.id
    );

    return;
  }

  const ext =
    typeof clipExt === "string" &&
    clipExt.startsWith(".")
      ? clipExt
      : (clipBlob.type || "").includes("webm")
      ? ".webm"
      : ".mp4";

  const boomerangClipPath =
    `events/${eventDocId}/${uid}/` +
    `boomerang_clips/${mediaDocRef.id}${ext}`;

  try {
    const storage = getStorage();

    const clipRef = storageRef(
      storage,
      boomerangClipPath
    );

    await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(
        clipRef,
        clipBlob,
        {
          contentType:
            clipBlob.type ||
            (ext === ".webm"
              ? "video/webm"
              : "video/mp4"),
        }
      );

      task.on(
        "state_changed",
        null,
        reject,
        resolve
      );
    });
  } catch (error) {
    console.error(
      "[boomerang] clip upload failed:",
      error
    );

    await updateDoc(
      doc(
        db,
        "events",
        eventDocId,
        "media",
        mediaDocRef.id
      ),
      {
        isBoomerang: false,
        boomerangStatus: null,
        boomerangUrl: null,
        boomerangClipPath: null,
      }
    );

    patchMediaItem(mediaDocRef.id, {
      isBoomerang: false,
      boomerangStatus: null,
      boomerangUrl: null,
      boomerangLocalUrl: null,
      isOptimistic: false,
    });

    releaseOptimisticBoomerangUrls(
      mediaDocRef.id
    );

    // The permanent hero photo remains safely in the gallery.
    return;
  }

  patchMediaItem(mediaDocRef.id, {
    isBoomerang: true,
    boomerangStatus: "processing",
  });

  try {
    await updateDoc(
      doc(
        db,
        "events",
        eventDocId,
        "media",
        mediaDocRef.id
      ),
      {
        boomerangClipPath,
        boomerangStatus: "processing",
      }
    );

    await startBoomerangRender({
      eventId: eventDocId,
      mediaId: mediaDocRef.id,
    });
  } catch (error) {
    console.error(
      "[boomerang] rendering failed:",
      error
    );

    patchMediaItem(mediaDocRef.id, {
      boomerangStatus: "error",
    });

    try {
      await updateDoc(
        doc(
          db,
          "events",
          eventDocId,
          "media",
          mediaDocRef.id
        ),
        {
          boomerangStatus: "error",
        }
      );
    } catch {}
  }
};

  const handlePhotoClick = (mediaId) => {
    let idx = gridPhotos.findIndex((p) => p.id === mediaId);
    if (idx !== -1) {
      setViewerActiveId(mediaId);
      setIndex(idx);
      setOpenPhotoViewer(true);
      return;
    }
    

    const clicked = filteredMedia.find((m) => m.id === mediaId);
    const targetUrl = clicked?.url || clicked?.thumbnailUrl || null;

    if (targetUrl) {
      idx = photosForViewer.findIndex((p) => p.url === targetUrl);
      if (idx !== -1) {
        setViewerActiveId(photosForViewer[idx]?.id || mediaId);
        setIndex(idx);
        setOpenPhotoViewer(true);
      }
    }
  };

const handleOpenTemplates = () => {
  setTemplatesOpen(true);
};


  

  const canDeleteMediaItem = (item) => {
    const uid = user?.uid;
    if (!uid) return false;

    if (forceStrict) return item.userId === uid || item.uploaderUid === uid;
    if (eventData && uid === eventData.userId) return true;
    if (item.userId === uid || item.uploaderUid === uid) return true;
    return false;
  };

  const handleDelete = async (mediaId) => {
  const item = media.find((m) => m.id === mediaId);
  if (!item) return;

  if (!window.confirm("Are you sure you want to delete this item?")) return;

  // Close the viewer before removing its active photo
  setOpenPhotoViewer(false);
  setViewerActiveId(null);

  // Just remove from UI. The real Firestore item
  // will arrive via snapshot.
  if (String(mediaId).startsWith("local-")) {
    setMedia((prev) =>
      prev.filter((m) => m.id !== mediaId)
    );
    return;
  }

  try {
    const currentUser = await ensureAuthReadyFast(
      ensureAnonUser,
      auth,
      600
    );

    setUser(currentUser);

    setMedia((prev) =>
      prev.filter((m) => m.id !== mediaId)
    );

    const mediaDocRef = doc(
      db,
      "events",
      eventDocId,
      "media",
      mediaId
    );

    await deleteDoc(mediaDocRef);
  } catch (error) {
    console.error("Error deleting media:", error);

    setMedia((prev) =>
      prev.some((m) => m.id === item.id)
        ? prev
        : [item, ...prev]
    );

    alert("Failed to delete media. Please try again.");
  }
};

  const customization = eventData?.customization || {};
  const PROFESSIONAL_TITLE_FONT =
    '"Fraunces","Inter","Manrope","SF Pro Display","Segoe UI","Helvetica Neue",Arial,sans-serif';

 const heroCustomization = useMemo(
  () => ({
    ...customization,
    titleFont: PROFESSIONAL_TITLE_FONT,
    titleFontColor: customization?.titleFontColor || "#111",
  }),
  [customization]
);

  const styleId =
  customization.coverStyleId ||
  customization.styleId ||
  eventData?.coverStyleId ||
  eventData?.styleId ||
  eventData?.customization?.coverStyleId ||
  "scarlet";

  const eventName = eventData?.details?.eventName || "Your Event";

  const coverSrcRaw =
    customization.coverPhotoUrl ||
    customization.coverPhotoDataUrl ||
    customization.coverPhotoPreviewUrl ||
    "";

  const coverSrc = coverSrcRaw || getIntentionalFallbackCover(eventName);
  const coverMissing = !coverSrcRaw;

  const coverZoom = customization.coverPhotoZoom !== undefined ? Number(customization.coverPhotoZoom) : 1;

  const containerStyles = {
    fontFamily: customization.titleFont || "Arial",
    fontSize: "16px",
    color: customization.titleFontColor || "#000",
    p: 0,
    mt: { xs: "-20px", sm: 0 },
    pb: "calc(112px + env(safe-area-inset-bottom))",
    minHeight: "100vh",
    backgroundColor,
    transition: "background-color 0.5s ease",
  };

  const getGridStyles = () => {
    const base =
      eventData?.gridStyle?.styles && Object.keys(eventData.gridStyle.styles).length > 0
        ? eventData.gridStyle.styles
        : {};
    return {
      ...base,
      display: "grid",
      gap: base.gap ?? 0,
      gridTemplateColumns: filterVibe === "all" ? "repeat(2, minmax(0, 1fr))" : "1fr",
    };
  };

  const norm = (s) => String(s || "").toLowerCase().replace(/[\s_-]+/g, "");
  const styleKey = norm(styleId);

  let primaryButtonBg = "#111";
  let primaryButtonText = "#fff";
  let secondaryBg = "#fff";
  let secondaryTextColor = "#111";
  let classicBorder = "rgba(0,0,0,0.18)";

  const preset =
    Array.isArray(STYLE_PRESETS)
      ? STYLE_PRESETS.find((p) => norm(p?.id) === styleKey || norm(p?.styleId) === styleKey || norm(p?.key) === styleKey)
      : null;

  const presetLabel = norm(preset?.label || preset?.name || preset?.title || preset?.displayName || "");

  if (preset?.buttonPrimaryBg) primaryButtonBg = preset.buttonPrimaryBg;
  if (preset?.buttonPrimaryText) primaryButtonText = preset.buttonPrimaryText;
  if (preset?.buttonSecondaryBg) secondaryBg = preset.buttonSecondaryBg;
  if (preset?.buttonSecondaryText) secondaryTextColor = preset.buttonSecondaryText;
  if (preset?.buttonBorder) classicBorder = preset.buttonBorder;

  const toSolid = (c, fallback) => (typeof c === "string" && c.trim().startsWith("rgba(") ? fallback : c);
  primaryButtonBg = toSolid(primaryButtonBg, "#111");
  secondaryBg = toSolid(secondaryBg, "#fff");

  const forceWhiteButtons =
    styleKey.includes("champagne") ||
    presetLabel.includes("champagne") ||
    styleKey.includes("polaroid") ||
    presetLabel.includes("polaroid") ||
    styleKey.includes("western") ||
    presetLabel.includes("western");

  if (forceWhiteButtons) {
    primaryButtonBg = "#fff";
    primaryButtonText = "#111";
    secondaryBg = "#fff";
    secondaryTextColor = "#111";
    classicBorder = "rgba(0,0,0,0.18)";
  }

  if (styleKey.includes("elegant") || styleKey.includes("sage") || styleKey.includes("pink")) {
    primaryButtonBg = "#fff";
    primaryButtonText = "#111";
    secondaryBg = "#fff";
    secondaryTextColor = "#111";
    classicBorder = "rgba(0,0,0,0.18)";
  }

  const tabBg = primaryButtonBg;
  const tabColor = primaryButtonText;
  const canExportPhotos =
  !manualGalleryHiddenForGuests && galleryState !== "STRICT_SELF_VIEW";

  /// ✅ shareUrl for menu drawer / QR code
// ✅ shareUrl for menu drawer / QR code
const shareUrl = useMemo(() => {
  if (typeof window === "undefined") return "";
  const base = window.location.origin || "";

  return eventDocId
    ? `${base}/events/${eventDocId}`
    : uniqueUrl
    ? `${base}/events/${uniqueUrl}`
    : "";
}, [eventDocId, uniqueUrl]);

 // Loading / error guards
// Only block the page while the EVENT is loading.
// Do not keep the whole page stuck because anonymous auth is slow.

// Loading / error guards
// IMPORTANT: once eventData exists, never cover the working page with skeleton/error.
if (!eventData && eventLoading && !eventError) {
  return <EventPageSkeleton backgroundColor={backgroundColor} />;
}

if (!eventData && eventError && !eventLoading) {
  const eventNotFound = eventError === "not-found";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor,
        px: 3,
        textAlign: "center",
      }}
    >
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {eventNotFound
          ? "This gallery could not be found."
          : "We couldn’t load this gallery."}
      </Typography>

      {!eventNotFound && (
        <>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            Check your connection and try again.
          </Typography>

          <Button
            variant="contained"
            onClick={retryEventLoad}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 4,
            }}
          >
            Try again
          </Button>
        </>
      )}
    </Box>
  );
}

if (!eventData) {
  return <EventPageSkeleton backgroundColor={backgroundColor} />;
}
  const renderMediaGrid = () => {
    if (galleryLoadError && visibleMedia.length === 0) {
  return (
    <Box sx={{ textAlign: "center", mt: 4, px: 2 }}>
      <Typography variant="h6" sx={{ color: "#666", mb: 1.5 }}>
        We couldn’t load the photos yet.
      </Typography>

      <Button
        variant="outlined"
        onClick={() => {
          setGalleryLoadError(false);
          setPageLoaded(false);
          setGalleryRetryKey((current) => current + 1);
        }}
        sx={{
          textTransform: "none",
          borderRadius: 999,
          px: 3,
        }}
      >
        Try again
      </Button>
    </Box>
  );
}
  if (!pageLoaded && visibleMedia.length === 0) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 1,
        mt: 3,
      }}
    >
      {[1, 2, 3, 4, 5, 6].map((x) => (
        <Box
          key={x}
          sx={{
            aspectRatio: "1 / 1",
            borderRadius: 2,
            backgroundColor: "rgba(0,0,0,0.06)",
          }}
        />
      ))}
    </Box>
  );
}

if (
  pageLoaded &&
  visibleMedia.length === 0 &&
  !isUploadingBatch &&
  !loading &&
  !isLoadingPage
) {
  // Surprise Mode only: show the fixed six-tile teaser.
  if (forceStrict && !planner) {
    return <StrictSurprisePreview />;
  }

  // Regular manually hidden galleries keep their existing UI.
  if (galleryHiddenByHost && !planner) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography
          variant="h6"
          sx={{ color: "#222", fontWeight: 600 }}
        >
          The gallery is private for now 👀
        </Typography>

        <Typography
          sx={{ mt: 0.75, color: "#777", fontSize: 15 }}
        >
          Share your photos to add your POV.
        </Typography>
      </Box>
    );
  }

  return (
    <Typography
      variant="h6"
      sx={{ textAlign: "center", mt: 4, color: "#666" }}
    >
      Help blow up this gallery 🔥
    </Typography>
  );
}
   return (
  <>
    <HiddenGalleryNotice
  planner={planner}
  galleryHiddenByHost={effectiveGalleryHiddenForGuests}
/>

{forceStrict && !planner && gridPhotos.length > 0 && (
  <Typography
    sx={{
      width: "min(720px, 95vw)",
      maxWidth: "100%",
      mx: "auto",
      mb: 1.25,
      color: "#111",
      fontSize: { xs: 20, sm: 22 },
      fontWeight: 700,
      lineHeight: 1.2,
    }}
  >
    Your uploads
  </Typography>
)}



    <div style={getGridStyles()}>
          {gridPhotos.map((item, idx) => (
            <MediaCard
  key={item.id}
  item={item}
  isPlanner={planner}
  canDelete={canDeleteMediaItem(item)}
  onDelete={() => handleDelete(item.id)}
  onPhotoClick={() => handlePhotoClick(item.id)}
  showUploaderName
  eager={idx < 4}
  priority={idx < 2}
/>
          ))}
        </div>
{forceStrict && !planner && (
  <StrictSurprisePreview mode="surprise" />
)}
        {/* ✅ Prefetch sentinel (invisible) */}
        <div ref={prefetchSentinelRef} style={{ width: "100%", height: 1, opacity: 0, pointerEvents: "none" }} />
{isLoadingPage && gridPhotos.length > 0 && (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      py: 3,
    }}
  >
    <CircularProgress
      size={20}
      thickness={4}
      sx={{ color: "rgba(0,0,0,0.35)" }}
    />
  </Box>
)}
        {!isLoadingPage && paginationError && hasMore && (
 <Box
  sx={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    my: 3,
    gap: 0.5,
  }}
>
  <Typography
    variant="body2"
    sx={{
      color: "#737373",
      fontFamily: "Arial, sans-serif",
    }}
  >
    Couldn’t load more
  </Typography>

  <Button
    variant="text"
    onClick={() => fetchPage(true)}
    sx={{
      textTransform: "none",
      minWidth: 0,
      p: 0,
      fontFamily: "Arial, sans-serif",
      fontWeight: 600,
      color: "#0095f6",
    }}
  >
    Try again
  </Button>
</Box>
)}
      </>
    );
  };

 const UploadProgressPill = () => {
  if (!isUploadingBatch) return null;

  const done = completedUploadsCount || 0;
  const total = totalUploadsCount || 0;

  const label =
    done <= 0
      ? "Preparing photos…"
      : total
      ? `Saving ${done} of ${total} photos…`
      : "Saving photos…";

  return (
    <Box
      sx={{
        position: "fixed",
        top: `calc(env(safe-area-inset-top, 0px) + 14px)`,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2200,
        width: "min(520px, calc(100vw - 24px))",
        px: 1.25,
      }}
    >
      <Box
        sx={{
          borderRadius: 999,
          px: 1.5,
          py: 1.1,
          bgcolor: "rgba(0,0,0,0.62)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.14)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
          color: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 800, lineHeight: 1.1 }}>
            {label}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const UploadSuccessToast = () =>
  showUploadSuccessMessage ? (
    <Box
      sx={{
        position: "fixed",
        top: `calc(env(safe-area-inset-top, 0px) + 68px)`,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2200,
        px: 2,
        py: 1.1,
        borderRadius: "999px",
        bgcolor: "rgba(0,0,0,0.78)",
        color: "#fff",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.14)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
        fontSize: "0.92rem",
        fontWeight: 800,
        letterSpacing: "-0.01em",
        textAlign: "center",
      }}
    >
      Your POV is live 🔥
    </Box>
  ) : null;

  const VideoBlockedBanner = () =>
    videoBlockedMessage ? (
      <Box
        sx={{
          position: "fixed",
          top: 12,
          left: "50%",
          transform: "translateX(-50%)",
          bgcolor: "rgba(0,0,0,0.85)",
          color: "#fff",
          borderRadius: "999px",
          px: 2,
          py: 1,
          zIndex: 2000,
          boxShadow: 3,
          fontWeight: 600,
          fontSize: 13,
        }}
      >
        {videoBlockedMessage}
      </Box>
    ) : null;

  // ✅ NEW: shared hamburger button (Instagram-style)
  const HamburgerButton = ({ variant = "hero" }) => (
    <IconButton
      aria-label="Open menu"
      onClick={() => setMenuOpen(true)}
      sx={{
        position: "fixed",
        top: `calc(env(safe-area-inset-top, 0px) + 14px)`,
        right: 14,
        zIndex: 2100,
        width: 44,
        height: 44,
        borderRadius: "999px",
        bgcolor: "rgba(0,0,0,0.35)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255,255,255,0.14)",
        "&:hover": { bgcolor: "rgba(0,0,0,0.42)" },
      }}
    >
      <MenuIcon sx={{ color: "#fff", fontSize: 22 }} />
    </IconButton>
  );

  // ----- LOCKED BRANCH (unchanged UI) -----
  if (showLockedUI && !bypassGate) {
    return (
      <>
        <VideoBlockedBanner />
        <UploadProgressPill />
        <UploadSuccessToast />
        <RevealCountdownBanner
          show={showRevealBanner && !isCameraOpen}
          revealAtMs={revealAtMs}
          message="Surprise mode: you can only see your uploads until the reveal."
        />

        {/* ✅ Instagram-style hamburger (lazy menu) */}
      {!openPhotoViewer && !isCameraOpen && !templatesOpen && (
  <HamburgerButton variant="locked" />
)}

        <input
          ref={fileInputRef}
          id="media-upload"
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={handleMediaUpload}
          disabled={isUploadingBatch || isCameraOpen}
        />

        <EventAccessUI
          event={eventForAccess}
          coverSrcOverride={coverSrc}
          coverMissing={coverMissing}
          coverStyleId={styleId}
          onUploadClick={openFilePicker}
          onTakePhotoClick={() => setIsCameraOpen(true)}
          onViewGalleryClick={() => {
            setBypassGate(true);
            setTimeout(() => {
              window.scrollTo({ top: window.innerHeight * 0.9, behavior: "smooth" });
            }, 50);
          }}
        />

        {isCameraOpen && (
          <React.Suspense fallback={null}>
            <CameraScreen
              eventId={eventDocId}
              eventName={eventData?.details?.eventName || "the Event"}
              planPriceId={
  eventData?.planPriceId ||
  eventData?.priceId ||
  eventData?.customization?.planPriceId ||
  eventData?.customization?.priceId ||
  ""
}
              uploadFn={handleCameraUpload}
              onClose={() => setIsCameraOpen(false)}
            />
          </React.Suspense>
        )}

       {emailGateOpen && (
  <React.Suspense fallback={null}>
    <EmailGate
      open={emailGateOpen}
      eventId={eventDocId}
      onClose={() => setEmailGateOpen(false)}
      onSuccess={() => {
        setUnlocked();

        if (pendingDownloadUrl && pendingFileName) {
          handleDownloadMedia(
            pendingDownloadUrl,
            pendingFileName
          );
        }

        setPendingDownloadUrl(null);
        setPendingFileName("");
        setEmailGateOpen(false);
      }}
    />
  </React.Suspense>
)}

        <Dialog
          open={detailsOpen}
          onClose={() => {
            setDetailsOpen(false);
            setStagedFiles([]);
          }}
          maxWidth="xs"
          fullWidth
        >
         <DialogTitle sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "1.25rem", textAlign: "center", pb: 0 }}>
  Make it yours 🔥
</DialogTitle>
<DialogContent sx={{ pt: 2, px: 3, display: "flex", flexDirection: "column", gap: 2 }}>
  <Typography variant="body2" sx={{ textAlign: "center", color: "text.secondary" }}>
    Add your name & set the vibe — then upload more 👀
  </Typography>
  <Typography variant="caption" sx={{ textAlign: "center", color: "text.secondary", mt: -1 }}>
    Keep them coming 👀
  </Typography>

            <Box sx={{ mt: 1 }}>
              <VibeSelector
                selectedVibe={selectedVibe}
                onVibeChange={setSelectedVibe}
                uploaderName={uploaderName}
                onNameChange={setUploaderName}
              />
            </Box>
          </DialogContent>

       <DialogActions
  sx={{
    px: 3,
    pb: 2.5,
    pt: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 1,
  }}
>
 <Button
  variant="contained"
  fullWidth
  disabled={isUploadingBatch || isStartingUpload || stagedFiles.length === 0}
  onClick={() => beginBatchUpload(stagedFiles)}
    sx={{
      borderRadius: 999,
      textTransform: "none",
      py: 1.2,
      fontWeight: 800,
      fontSize: "1rem",
      boxShadow: "none",
    }}
  >
   {isStartingUpload ? "Starting upload…" : "Upload"}
  </Button>

  <Button
    onClick={() => {
      setSelectedVibe("");
      beginBatchUpload(stagedFiles);
    }}
    sx={{
      textTransform: "none",
      fontWeight: 600,
      color: "text.secondary",
      "&:hover": {
        backgroundColor: "transparent",
        color: "text.primary",
      },
    }}
  >
    Skip for now
  </Button>
</DialogActions>
        </Dialog>

        {/* ✅ Menu Drawer is lazy-loaded (connects to your EventMenuDrawer + EventShareSheet inside it) */}
        {menuOpen && (
          <React.Suspense fallback={null}>
            <EventMenuDrawer
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              eventName={eventName}
              shareUrl={shareUrl}
            />
          </React.Suspense>
        )}
      </>
    );
  }

  return (
    <div style={{ backgroundColor, minHeight: "100vh", transition: "background-color 0.5s ease" }}>
      <VideoBlockedBanner />
      <UploadProgressPill />
      <UploadSuccessToast />
      <RevealCountdownBanner
        show={showRevealBanner && !isCameraOpen}
        revealAtMs={revealAtMs}
        message="Surprise mode: you can only see your uploads until the reveal."
      />

      {/* ✅ Instagram-style hamburger (lazy menu) */}
    {!openPhotoViewer && !isCameraOpen && !templatesOpen && (
  <HamburgerButton variant="main" />
)}

      <input
        ref={fileInputRef}
        id="media-upload"
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={handleMediaUpload}
        disabled={isUploadingBatch || isCameraOpen}
      />

      <Container sx={containerStyles} disableGutters>
        <div className="event-header" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
  {(coverSrc || hasAnyPhotos) && (
    <LiveCoverHero
      coverSrc={coverSrc}
      coverZoom={coverZoom}
      liveCoverSrc={liveCoverSrc}
      coverMissing={coverMissing}
      hasAnyPhotos={hasAnyPhotos}
      details={eventData?.details}
      customization={heroCustomization}
      stickyHeight={STICKY_VIBE_BAR_H}
      >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          px: { xs: 2, sm: 2.25 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1.5,
            width: "100%",
          }}
        >
          <Button
  variant="contained"
  onClick={openFilePicker}
  startIcon={<IosShareOutlinedIcon />}
  sx={{
    textTransform: "none",
    borderRadius: 999,
   fontWeight: 600,
px: 2,
height: 46,
minHeight: 46,
fontSize: "0.9rem",
    lineHeight: 1,
    bgcolor: "#fff",
    color: "#111",
    border: "1px solid rgba(255,255,255,0.92)",
   boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
    transition: "transform 140ms ease, background-color 140ms ease",

    "& .MuiButton-startIcon": {
      mr: 1,
      ml: 0,
      "& svg": {
        fontSize: 20,
      },
    },

    "&:hover": {
      bgcolor: "rgba(255,255,255,0.94)",
      boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
    },

    "&:active": {
      transform: "scale(0.98)",
    },
  }}
>
  Upload
</Button>

<Button
  variant="outlined"
  onClick={() => setIsCameraOpen(true)}
  startIcon={<CameraAltOutlinedIcon />}
  sx={{
    textTransform: "none",
    borderRadius: 999,
    fontWeight: 600,
   px: 2,
height: 46,
minHeight: 46,
fontSize: "0.9rem",
    lineHeight: 1,
   bgcolor: "rgba(15,15,15,0.42)",
    color: "#fff",
    border: "1.25px solid rgba(255,255,255,0.68)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    transition: "transform 140ms ease, background-color 140ms ease",

    "& .MuiButton-startIcon": {
      mr: 1,
      ml: 0,
      "& svg": {
        fontSize: 20,
      },
    },

    "&:hover": {
      bgcolor: "rgba(15,15,15,0.50)",
      borderColor: "rgba(255,255,255,0.78)",
    },

    "&:active": {
      transform: "scale(0.98)",
    },
  }}
>
  POV Shot
</Button>
        </Box>
      </Box>
    </LiveCoverHero>
  )}
</div>
        <Box sx={{ height: STICKY_VIBE_BAR_H }} />

        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200,
            backgroundColor: "transparent",
            px: 2,
            py: 1,
            paddingBottom: "env(safe-area-inset-bottom)",
            pointerEvents: "none",
          }}
        >
          <Box sx={{ maxWidth: 980, mx: "auto", position: "relative" }}>
            <Box
              ref={scrollRef}
              sx={{
                pointerEvents: "auto",
                overflowX: "auto",
                overscrollBehaviorX: "contain",
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <Box sx={{ display: "inline-flex", minWidth: "max-content" }}>
                <VibeFilterBar
                  media={visibleMedia}
                  mediaForCounts={countSourcePhotos}
                  overrideAllCount={photoCountServer ?? undefined}
                  filterVibe={filterVibe}
                  onFilterChange={setFilterVibe}
                  tabBg={tabBg}
                  tabColor={tabColor}
                  variant="bare"
                />
              </Box>
            </Box>

            {showVibeArrow && (
              <Box
                sx={{
                  position: "absolute",
                  right: 4,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 28,
                  height: 28,
                  borderRadius: "999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(0,0,0,0.45)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 18, color: "#fff" }} />
              </Box>
            )}
          </Box>
        </Box>

              <Box
  sx={{
    mt: { xs: 0, sm: 2 },
mb: 1.2,
    width: "min(720px, 95vw)",
    maxWidth: "100%",
    mx: "auto",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "stretch",
    bgcolor: "#fff",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
  }}
>
  <Button
    onClick={() => setStorybookOpen(true)}
    startIcon={
  <svg
    width="21"
    height="21"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3.5 5.5c2.8-.8 5.4-.3 8.5 1.5v13c-3.1-1.8-5.7-2.3-8.5-1.5z" />
    <path d="M20.5 5.5c-2.8-.8-5.4-.3-8.5 1.5v13c3.1-1.8 5.7-2.3 8.5-1.5z" />
  </svg>
}
    sx={{
      flex: "1 1 0",
      minWidth: 0,
      height: 52,
      borderRadius: 0,
      border: "none",
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.82rem",
      color: "#111",
      bgcolor: "transparent",
      boxShadow: "none",

      "&:hover": {
        bgcolor: "rgba(0,0,0,0.035)",
        boxShadow: "none",
      },

      "&:active": {
        bgcolor: "rgba(0,0,0,0.07)",
      },
    }}
  >
    Photobook
  </Button>

  <Box
    sx={{
      width: "1px",
      height: 24,
      alignSelf: "center",
      flexShrink: 0,
      bgcolor: "rgba(0,0,0,0.12)",
    }}
  />

  <Button
    onClick={handleOpenTemplates}
   startIcon={
  <svg
    width="21"
    height="21"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 2.75c.55 4.15 2.1 5.7 6.25 6.25-4.15.55-5.7 2.1-6.25 6.25C11.45 11.1 9.9 9.55 5.75 9 9.9 8.45 11.45 6.9 12 2.75Z" />
    <path d="M19 14.5c.25 1.9.95 2.6 2.85 2.85-1.9.25-2.6.95-2.85 2.85-.25-1.9-.95-2.6-2.85-2.85 1.9-.25 2.6-.95 2.85-2.85Z" />
  </svg>
}
    sx={{
      flex: "1 1 0",
      minWidth: 0,
      height: 52,
      borderRadius: 0,
      border: "none",
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.82rem",
      color: "#111",
      bgcolor: "transparent",
      boxShadow: "none",

      "& .MuiButton-startIcon": {
        mr: 0.5,
      },

      "&:hover": {
        bgcolor: "rgba(0,0,0,0.035)",
        boxShadow: "none",
      },

      "&:active": {
        bgcolor: "rgba(0,0,0,0.07)",
      },
    }}
  >
    Templates
  </Button>

  {canExportPhotos && (
    <>
      <Box
        sx={{
          width: "1px",
          height: 24,
          alignSelf: "center",
          flexShrink: 0,
          bgcolor: "rgba(0,0,0,0.12)",
        }}
      />

      <Button
        onClick={() => setExportOpen(true)}
disabled={!eventDocId}
       startIcon={
  <svg
    width="21"
    height="21"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3v12" />
    <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
    <path d="M5 15.5v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3" />
  </svg>
}
        sx={{
          flex: "1 1 0",
          minWidth: 0,
          height: 52,
          borderRadius: 0,
          border: "none",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.82rem",
          color: "#111",
          bgcolor: "transparent",
          boxShadow: "none",

          "& .MuiButton-startIcon": {
            mr: 0.5,
          },

          "&:hover": {
            bgcolor: "rgba(0,0,0,0.035)",
            boxShadow: "none",
          },

          "&:active": {
            bgcolor: "rgba(0,0,0,0.07)",
          },
        }}
      >
       Export
      </Button>
    </>
  )}
</Box>

<div
  className="media-gallery"
  style={{
    marginTop: "18px",
    contentVisibility: "visible",
    contain: "none",
  }}
>
  {renderMediaGrid()}
</div>

      <React.Suspense fallback={null}>
  <FullscreenMediaViewer
    open={openPhotoViewer}
    photos={photosForViewer}
    index={index}
    eventName={eventData?.details?.eventName || "Event"}
    eventUrl={
      typeof window !== "undefined"
        ? window.location.href
        : ""
    }
    unlocked={isUnlocked()}
    canDelete={canDeleteMediaItem}
    onIndexChange={(nextIndex) => {
      setIndex(nextIndex);
      setViewerActiveId(
        photosForViewer[nextIndex]?.id || null
      );
    }}
    onClose={() => {
      setOpenPhotoViewer(false);
      setViewerActiveId(null);
    }}
    onLockedAction={() => {
      setEmailGateOpen(true);
    }}
    onDownload={(item, photoIndex) => {
  const isBoomerang =
    item?.isBoomerang === true ||
    item?.boomerangStatus === "processing";

  if (isBoomerang && !isHttpish(item?.boomerangUrl)) {
    alert("Boomerang is still processing. Please try again shortly.");
    return;
  }

  const url = isBoomerang
    ? item.boomerangUrl
    : item?.viewerStillUrl || item?.url || item?.thumbnailUrl;

  if (!url) {
    alert("This photo is not available to save.");
    return;
  }

  const eventFileName =
    eventData?.details?.eventName?.replace(/\s+/g, "_") || "event";

  const filename = isBoomerang
    ? `${eventFileName}_boomerang_${photoIndex + 1}.mp4`
    : `${eventFileName}_photo_${photoIndex + 1}.jpg`;

  if (isUnlocked()) {
    handleDownloadMedia(url, filename);
  } else {
    setPendingDownloadUrl(url);
    setPendingFileName(filename);
    setEmailGateOpen(true);
  }
}}
    onDelete={(item) => {
      if (!item?.id) return;
      handleDelete(item.id);
    }}
  />
</React.Suspense>

        {isCameraOpen && (
          <React.Suspense fallback={null}>
            <CameraScreen
              eventId={eventDocId}
              eventName={eventData?.details?.eventName || "the Event"}
              planPriceId={
  eventData?.planPriceId ||
  eventData?.priceId ||
  eventData?.customization?.planPriceId ||
  eventData?.customization?.priceId ||
  ""
}
              uploadFn={handleCameraUpload}
              onClose={() => setIsCameraOpen(false)}
            />
          </React.Suspense>
        )}

        {emailGateOpen && (
  <React.Suspense fallback={null}>
    <EmailGate
      open={emailGateOpen}
      eventId={eventDocId}
      onClose={() => setEmailGateOpen(false)}
      onSuccess={() => {
        setUnlocked();

        if (pendingDownloadUrl && pendingFileName) {
          handleDownloadMedia(
            pendingDownloadUrl,
            pendingFileName
          );
        }

        setPendingDownloadUrl(null);
        setPendingFileName("");
        setEmailGateOpen(false);
      }}
    />
  </React.Suspense>
)}

        <Dialog
          open={detailsOpen}
          onClose={() => {
            setDetailsOpen(false);
            setStagedFiles([]);
          }}
          maxWidth="xs"
          fullWidth
        >
         <DialogTitle sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "1.25rem", textAlign: "center", pb: 0 }}>
  Make it yours 🔥
</DialogTitle>
<DialogContent sx={{ pt: 2, px: 3, display: "flex", flexDirection: "column", gap: 2 }}>
  <Typography variant="body2" sx={{ textAlign: "center", color: "text.secondary" }}>
    Add your name + vibe, then upload more.
  </Typography>
  <Typography variant="caption" sx={{ textAlign: "center", color: "text.secondary", mt: -1 }}>
    Keep them coming 👀
  </Typography>
            <Box sx={{ mt: 1 }}>
              <VibeSelector
                selectedVibe={selectedVibe}
                onVibeChange={setSelectedVibe}
                uploaderName={uploaderName}
                onNameChange={setUploaderName}
              />
            </Box>
          </DialogContent>
         <DialogActions
  sx={{
    px: 3,
    pb: 2.5,
    pt: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 1,
  }}
>
  <Button
    variant="contained"
    fullWidth
    onClick={() => beginBatchUpload(stagedFiles)}
    sx={{
      borderRadius: 999,
      textTransform: "none",
      py: 1.2,
      fontWeight: 800,
      fontSize: "1rem",
      boxShadow: "none",
    }}
  >
    Upload
  </Button>

  <Button
    onClick={() => {
      setSelectedVibe("");
      beginBatchUpload(stagedFiles);
    }}
    sx={{
      textTransform: "none",
      fontWeight: 600,
      color: "text.secondary",
      "&:hover": {
        backgroundColor: "transparent",
        color: "text.primary",
      },
    }}
  >
    Skip for now
  </Button>
</DialogActions>
        </Dialog>


{templatesOpen && (
  <React.Suspense fallback={null}>
    <TemplateFlowLauncher
      open={templatesOpen}
      onClose={() => setTemplatesOpen(false)}
      photos={photosForViewer}
      eventName={eventData?.details?.eventName}
      eventDate={eventData?.details?.eventDate}
    />
  </React.Suspense>
)}

        {/* Storybook drawer */}
<StorybookDrawer
  open={storybookOpen}
  event={eventForAccess}
  coverSrcOverride={coverSrc}
  onClose={() => {
    setStorybookShowFaq(false);
    setStorybookOpen(false);
  }}
  storybookShowFaq={storybookShowFaq}
  setStorybookShowFaq={setStorybookShowFaq}
  storybookLabelCurrent={storybookLabelCurrent}
  storybookTarget={storybookTarget}
  storybookPct={storybookPct}
  storybookLocked={storybookLocked}
  sbCheckoutLoading={sbCheckoutLoading}
  onCheckout={handleStorybookCheckout}
/>

        {/* ✅ Menu Drawer is lazy-loaded (connects to your EventMenuDrawer + EventShareSheet inside it) */}
        {menuOpen && (
          <React.Suspense fallback={null}>
            <EventMenuDrawer open={menuOpen} onClose={() => setMenuOpen(false)} eventName={eventName} shareUrl={shareUrl} />
          </React.Suspense>
        )}
        {exportOpen && (
  <React.Suspense fallback={null}>
    <ExportFlowDrawer
      open={exportOpen}
      onClose={() => setExportOpen(false)}
      eventId={eventDocId}
      eventName={eventData?.details?.eventName}
      isHost={planner}
      galleryHiddenForGuests={manualGalleryHiddenForGuests}
      forceStrict={forceStrict}
      userId={user?.uid}
    />
  </React.Suspense>
)}
      </Container>

      <Backdrop open={sbCheckoutLoading} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 2 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default EventPage;